import { useState, useCallback, useRef, useEffect } from "react";

type Props = {
  id: string;
  content: string;
  x: number;
  y: number;
  width: number;
  zIndex: number;
  color: string;
  isSelected: boolean;
  canEdit: boolean;
  zoom: number;
  isNew?: boolean;
  onSelect: () => void;
  onUpdate: (updates: { content?: string; x?: number; y?: number; width?: number; color?: string }) => void;
  onDelete: () => void;
  onBringToFront: () => void;
};

const NOTE_COLORS = [
  { name: "Yellow", value: "#fbbf24" },
  { name: "Orange", value: "#fb923c" },
  { name: "Pink", value: "#f472b6" },
  { name: "Purple", value: "#a78bfa" },
  { name: "Blue", value: "#60a5fa" },
  { name: "Green", value: "#4ade80" },
];

export function DraggableNote({
  id,
  content,
  x,
  y,
  width,
  zIndex,
  color,
  isSelected,
  canEdit,
  zoom,
  isNew,
  onSelect,
  onUpdate,
  onDelete,
  onBringToFront,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ mouseX: 0, mouseY: 0, elemX: 0, elemY: 0 });
  const [isEditing, setIsEditing] = useState(isNew || false);
  const [editedContent, setEditedContent] = useState(content);
  const [isHovered, setIsHovered] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Auto-focus textarea when editing
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  // Drag handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!canEdit) return;
    if ((e.target as HTMLElement).closest('button, textarea, .color-picker')) return;

    e.preventDefault();
    e.stopPropagation();
    onSelect();
    onBringToFront();
    setIsDragging(true);
    setDragStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      elemX: x,
      elemY: y,
    });
  }, [canEdit, x, y, onSelect, onBringToFront]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = (e.clientX - dragStart.mouseX) / zoom;
      const dy = (e.clientY - dragStart.mouseY) / zoom;
      const newX = Math.max(0, dragStart.elemX + dx);
      const newY = Math.max(0, dragStart.elemY + dy);

      if (containerRef.current) {
        containerRef.current.style.transform = `translate(${newX}px, ${newY}px)`;
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      const dx = (e.clientX - dragStart.mouseX) / zoom;
      const dy = (e.clientY - dragStart.mouseY) / zoom;
      const newX = Math.max(0, Math.round(dragStart.elemX + dx));
      const newY = Math.max(0, Math.round(dragStart.elemY + dy));
      onUpdate({ x: newX, y: newY });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, zoom, onUpdate]);

  const handleContentSave = useCallback(() => {
    setIsEditing(false);
    if (editedContent !== content) {
      onUpdate({ content: editedContent });
    }
  }, [editedContent, content, onUpdate]);

  const handleColorChange = useCallback((newColor: string) => {
    setShowColorPicker(false);
    onUpdate({ color: newColor });
  }, [onUpdate]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (!canEdit) return;
    e.stopPropagation();
    setIsEditing(true);
  }, [canEdit]);

  // Get background color with opacity
  const bgColor = color || "#fbbf24";
  const bgColorWithOpacity = `${bgColor}33`; // 20% opacity
  const borderColor = `${bgColor}66`; // 40% opacity

  return (
    <div
      ref={containerRef}
      className={`absolute group draggable-item ${isDragging ? 'cursor-grabbing' : canEdit ? 'cursor-grab' : ''}`}
      style={{
        transform: `translate(${x}px, ${y}px)`,
        width,
        zIndex,
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowColorPicker(false);
      }}
    >
      <div
        className={`relative rounded-lg overflow-hidden transition-shadow ${
          isSelected ? 'ring-2 ring-primary-500' : ''
        }`}
        style={{
          backgroundColor: bgColorWithOpacity,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: borderColor,
        }}
      >
        {/* Header bar for dragging */}
        <div
          className="h-6 flex items-center justify-between px-2"
          style={{ backgroundColor: borderColor }}
        >
          <div className="flex-1" />
          {canEdit && isHovered && (
            <div className="flex items-center gap-1">
              {/* Color picker button */}
              <div className="relative color-picker">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowColorPicker(!showColorPicker);
                  }}
                  className="p-0.5 hover:bg-white/20 rounded transition-colors"
                  title="Change color"
                >
                  <div
                    className="w-3 h-3 rounded-full border border-white/40"
                    style={{ backgroundColor: bgColor }}
                  />
                </button>
                {showColorPicker && (
                  <div className="absolute top-full right-0 mt-1 p-2 bg-surface border border-border rounded-lg shadow-lg z-10 flex gap-1.5">
                    {NOTE_COLORS.map((c) => (
                      <button
                        key={c.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleColorChange(c.value);
                        }}
                        className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${
                          c.value === bgColor ? 'border-white' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: c.value }}
                        title={c.name}
                      />
                    ))}
                  </div>
                )}
              </div>
              {/* Delete button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-0.5 hover:bg-white/20 rounded transition-colors"
                title="Delete note"
              >
                <svg className="w-3 h-3 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Content area */}
        <div className="p-3">
          {isEditing && canEdit ? (
            <textarea
              ref={textareaRef}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              onBlur={handleContentSave}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setEditedContent(content);
                  setIsEditing(false);
                }
                // Allow Enter for newlines in notes
              }}
              className="w-full bg-transparent text-sm text-foreground resize-none focus:outline-none min-h-[60px]"
              placeholder="Type your note..."
              style={{ minHeight: 60 }}
            />
          ) : (
            <div
              className={`text-sm whitespace-pre-wrap min-h-[60px] ${
                content ? 'text-foreground' : 'text-muted/50 italic'
              }`}
            >
              {content || (canEdit ? "Double-click to edit..." : "")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
